/* Role permissions for the UI — single source of truth.
 *
 * WHY THIS EXISTS
 * The same stale role check was copy-pasted into six components. When the
 * Accounts role was introduced the server gates were updated and the UI was
 * not, so Accounts users saw controls that were disabled or hidden for actions
 * the server already permitted (Tiffany could not approve month-end: the button
 * was `disabled`, so the click never fired a request and there was no error to
 * see — a dead control, not a failed call).
 *
 * RULE: every entry below mirrors the role tuple on the corresponding server
 * endpoint in backend/server.py. The route and its line number are in the
 * comment so the two can be diffed by eye. If you change a gate on the server,
 * change it here in the same pass.
 *
 * DO NOT widen an entry past what the server allows — that recreates the
 * inverse bug: a live control that 403s.
 */

const DIRECTOR = "Director";
const OFFICE = "Office";
const ACCOUNTS = "Accounts";

export const POLICY = {
  // --- month-end -----------------------------------------------------------
  // POST /month-end/{year}/{month}/approve            server.py:2400 (deprecated 7 Sep 2026)
  "month-end.approve": [DIRECTOR, OFFICE, ACCOUNTS],
  // POST /month-end/{year}/{month}/send               new 7 Sep 2026 — girls' daily send batch
  "month-end.send":    [DIRECTOR, OFFICE, ACCOUNTS],
  // POST /month-end/{year}/{month}/close              new 7 Sep 2026 — locks the month
  "month-end.close":   [DIRECTOR],

  // POST /month-end/{year}/{month}/adjustments        server.py:1310
  // Director + Accounts only — Office is NOT permitted to adjust a locked
  // month, on the server or here. (The server’s 403 message still reads "Only
  // a director can add an adjustment", which is stale copy now that Accounts
  // is allowed; backend text, not changed in this frontend-only pass.)
  "month-end.adjustments": [DIRECTOR, ACCOUNTS],

  // --- quotes --------------------------------------------------------------
  // POST /quotes 3974 · PATCH /quotes/{id} 4020 · POST /quotes/{id}/send 4048
  "quotes.manage": [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /quotes/{quote_id}/convert-to-hire           server.py:4092
  // Accounts is NOT permitted server-side. Converting a quote creates a hire,
  // and POST /hires (1094) excludes Accounts too — Accounts does billing, not
  // fleet or hire creation. Split out of quotes.manage so opening the quote
  // controls to Accounts does not expose a button that 403s.
  "quotes.convert-to-hire": [DIRECTOR, OFFICE],

  // --- hires ---------------------------------------------------------------
  // POST /hires/{hire_id}/off-hire                    server.py:1167
  "hires.off-hire": [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /hires/{hire_id}/stand-downs                 server.py:3065
  "hires.stand-downs": [DIRECTOR, OFFICE, ACCOUNTS],

  // DELETE /hires/{hire_id}/stand-downs/{sd_id}       server.py:delete_stand_down (7 Sep 2026)
  // Correction control — separate entry from hires.stand-downs so if either
  // server gate widens or tightens it can move here without dragging the other.
  "hires.stand-downs.delete": [DIRECTOR, OFFICE, ACCOUNTS],
  // PATCH  /hires/{hire_id}/stand-downs/{sd_id}       server.py:edit_stand_down (7 Sep 2026)
  "hires.stand-downs.edit":   [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /hires/{hire_id}/off-hire (amendment path)   server.py:off_hire (rewritten 7 Sep 2026)
  // Same roles as the fresh off-hire above — the protection here is the gate,
  // not the role, per Todd. Kept as a separate action so UX copy change
  // ("Adjust off-hire date" vs "Off-hire") does not couple the two.
  "hires.off-hire.adjust": [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /hires/{hire_id}/reopen                      server.py:reopen_hire (new 7 Sep 2026)
  "hires.reopen":         [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /hires/{hire_id}/swap-machine                server.py:3168
  // Accounts is NOT permitted server-side. Same reason as convert-to-hire:
  // swapping the machine on a job is fleet work. Split out of the off-hire
  // flag, which previously gated this button too.
  "hires.swap-machine": [DIRECTOR, OFFICE],

  // POST /hires/{hire_id}/rate-change                 server.py:1220
  // Director + Accounts only — Office is NOT permitted, on the server or here.
  // Same stale 403 copy as adjustments ("Only a director can change a hire’s
  // rate"); backend text, left alone in this frontend-only pass.
  "hires.rate-change": [DIRECTOR, ACCOUNTS],

  // PATCH /hires/{hire_id}                            server.py:4195 (edit_hire)
  // Six informational fields only — expected_end_date, continuous_hire,
  // site_location, po_number, transport_provider, notes. Rule confirmed by
  // Todd on 8 Sept 2026: anything that describes the job can be corrected at
  // any time; anything that changes what was billed is frozen once the
  // invoice has gone and goes through its own guarded endpoint (rate-change,
  // start-date-change, adjustments, custom-charges, off-hire, stand-downs).
  // So this action is NOT billed-state-gated — the check is purely role.
  "hires.edit":          [DIRECTOR, OFFICE, ACCOUNTS],

  // --- customers -----------------------------------------------------------
  // PATCH /customers/{customer_id}                    server.py:3553
  // Read-only can view the customers list but not edit; the server 403s,
  // and the UI disables the Edit / Save buttons rather than presenting a
  // control that fails when clicked.
  "customers.edit":      [DIRECTOR, OFFICE, ACCOUNTS],
  // POST /customers/{customer_id}/reminders           server.py:4145
  "customers.reminders": [DIRECTOR, OFFICE, ACCOUNTS],

  // POST /hires/{hire_id}/comments                    server.py:add_hire_comment (1 Oct 2026)
  // Everyone can READ hire comments — the comments ride with the hire doc
  // served to any authenticated user. Only Director/Office/Accounts can WRITE
  // because a comment is a persistent record visible to everyone else, so it's
  // a write by any reasonable definition. Read-only remains read-only.
  "hires.comments.add":  [DIRECTOR, OFFICE, ACCOUNTS],

  // DELETE /hires/{hire_id}/comments/{id}             server.py:soft_delete_hire_comment (1 Oct 2026)
  // Soft-delete — the record stays in the DB with deleted_at/deleted_by.
  // Office/Accounts can only remove their OWN comments (self-service tidy-up).
  // Director can remove ANY comment (so a lead can clean up anybody's note on
  // Todd's instruction). Read-only cannot delete at all — same reason they
  // cannot add.
  "hires.comments.delete-own":  [DIRECTOR, OFFICE, ACCOUNTS],
  "hires.comments.delete-any":  [DIRECTOR],
};

/* Roles that must never reach a page whose load() calls a Director-only
   endpoint. Used by the sidebar (hideFor) and the route guard. */
export const PAGE_BLOCKED = {
  // Settings loads /settings/users and /connections, both Director-only, so
  // the page throws for anyone else. It is genuinely Director-only
  // functionality, so it is hidden rather than made to degrade.
  "/settings": [ACCOUNTS],
};

export function can(user, action) {
  const allowed = POLICY[action];
  if (!allowed) {
    // Unknown action: deny, and make the mistake visible in development
    // rather than silently granting or silently blocking.
    if (typeof console !== "undefined") {
      console.warn(`[roles] unknown action "${action}" — denying`);
    }
    return false;
  }
  return !!user && allowed.includes(user.role);
}

export function pageBlockedFor(path, user) {
  return (PAGE_BLOCKED[path] || []).includes(user?.role);
}
